/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { FaqModuleItem } from '@coursera-web/cds-labs';
import { FAQsModule } from '@coursera-web/cds-labs';
import type { CmlContent } from '__generated__/graphql-types-do-not-use';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { CML, toPlainText } from 'bundles/cml';
import FinaidLink from 'bundles/enroll-common/components/finaid/FinaidLink';
import PartnerSupportedAccessLink from 'bundles/enroll-common/components/freemium/PartnerSupportedAccessLink';
import CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import OnDemandSpecializationsV1 from 'bundles/naptimejs/resources/onDemandSpecializations.v1';
import FaqsSchemaMarkup from 'bundles/seo/components/FaqsSchemaMarkup';
import HelpCenterInfo from 'bundles/unified-common/components/HelpCenterInfo';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import convertToCmlContent from 'bundles/unified-description-page-common/utils/convertToCmlContent';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment | ConsumerSpecializationPageFieldsFragment;
  config?: {
    disableCta?: boolean;
  };
};

const styles = {
  finAid: (enableXDPRedesign: boolean) => css`
    .rc-FinaidLink {
      display: flex;
      ${enableXDPRedesign
        ? xdpStyles.border('border-top')
        : 'border-top: 2px solid var(--cds-color-neutral-stroke-primary-weak);'}
      margin-top: var(--cds-spacing-300);
      padding-top: var(--cds-spacing-300);
    }

    .caption-text,
    button.finaid-link {
      ${typography2.bodyPrimary}
      font-size: var(--cds-font-size-175) !important;
      margin-top: 0;
    }

    .caption-text {
      color: var(--cds-color-neutral-primary-weak);
    }
  `,
  partnerSupportedFreeAccess: css`
    margin-top: var(--cds-spacing-100);
    font-size: var(--cds-font-size-175);
    color: var(--cds-color-neutral-primary-weak);
  `,
  overrides: css`
    margin-bottom: calc(var(--cds-spacing-400) * -1);

    ${breakpoints.down('sm')} {
      margin-bottom: calc(var(--cds-spacing-300) * -1);
    }

    h2 {
      ${typography2.subtitleLarge}
      margin-bottom: calc((var(--cds-spacing-400) - 20px) * -1);
      margin-top: 0;
    }
  `,
};

const FAQsModuleRenderer = ({ pageData, config }: Props) => {
  const eventingProductData = useEventingProductData();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'faq',
      },
      ...eventingProductData,
    }
  );

  const convertedFaqs: FaqModuleItem[] = [];
  const textFaqs: { question: string; answer: string }[] = [];
  pageData.cmlFaqs?.forEach((faq) => {
    const question = toPlainText(convertToCmlContent(faq.question as CmlContent)) || '';

    convertedFaqs.push({
      question,
      answer: <CML cml={faq.answer as CmlContent} />,
    });

    textFaqs.push({
      question,
      answer: toPlainText(convertToCmlContent(faq.answer as CmlContent)) || '',
    });
  });

  let finAidProps;
  let partnerSupportedAccessProps;
  if (pageData.__typename === 'DescriptionPage_CoursePage') {
    partnerSupportedAccessProps = {
      courseId: pageData.id,
      courseSlug: pageData.slug,
    };
    finAidProps = {
      courseId: pageData.id,
      course: new CoursesV1({
        slug: pageData.slug,
        id: pageData.id,
        name: pageData.name,
        courseType: pageData.courseType,
        courseStatus: pageData.courseStatus,
        certificates: pageData.certificates,
        premiumExperienceVariant: pageData.premiumExperienceVariant,
      }),
    };
  } else if (pageData.__typename === 'DescriptionPage_SpecializationPage') {
    finAidProps = {
      s12nId: pageData.id,
      courseIds: pageData.courses?.map(({ id }: { id: string }) => id),
      s12n: new OnDemandSpecializationsV1({
        slug: pageData.slug,
        id: pageData.id,
        name: pageData.name,
        productVariant: pageData.productVariant,
      }),
    };
  }

  return (
    <Section ref={visibilityTrackingRef}>
      <div css={enableXDPRedesign ? styles.overrides : undefined}>
        <FAQsModule
          faqs={convertedFaqs}
          accordionTrackingData={eventingProductData}
          removeBottomPadding={enableXDPRedesign}
          removeTopPadding={enableXDPRedesign}
          moreInfo={
            !config?.disableCta && (
              <>
                <HelpCenterInfo />
                {finAidProps && (
                  <Typography2 variant="bodyPrimary" component="span" css={styles.finAid(enableXDPRedesignPhase1)}>
                    <FinaidLink
                      {...finAidProps}
                      canOpenFinAidOnMount={false}
                      applyTextComponent={(link: React.ReactNode) => {
                        return <FormattedMessage message={_t(`Financial aid available, {link}`)} link={link} />;
                      }}
                      applyText={_t('learn more')}
                      alreadyAppliedText=""
                      wrapperClass="reset"
                    />
                  </Typography2>
                )}
                <Typography2 variant="bodyPrimary" component="div" css={styles.partnerSupportedFreeAccess}>
                  <PartnerSupportedAccessLink {...partnerSupportedAccessProps} />
                </Typography2>
              </>
            )
          }
        />
        <FaqsSchemaMarkup faqs={textFaqs} />
      </div>
    </Section>
  );
};

export default FAQsModuleRenderer;
